import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { queryOptions, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, type FormEvent } from "react";
import { CheckCircle2, ChevronRight, Mail, Phone, ShieldCheck, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  addWorkPhoto,
  deleteWorkPhoto,
  getMyPartnerProfile,
  getMyPartnerVerificationStatus,
  listMyWorkPhotos,
  upsertMyPartnerProfile,
} from "@/lib/partner.functions";
import { uploadPhoto } from "@/lib/uploads";
import { getSessionInfo } from "@/lib/session.functions";
import { listServices } from "@/lib/services.functions";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { OperatingArea } from "@/components/partner/OperatingArea";
import { AppHeader } from "@/components/site/AppHeader";
import { DisclosureCard } from "@/components/DisclosureCard";

const sessionQuery = queryOptions({ queryKey: ["session-info"], queryFn: () => getSessionInfo() });
const servicesQuery = queryOptions({ queryKey: ["services"], queryFn: () => listServices() });
const profileQuery = queryOptions({
  queryKey: ["my-partner-profile"],
  queryFn: () => getMyPartnerProfile(),
});
const verificationQuery = queryOptions({
  queryKey: ["my-partner-verification"],
  queryFn: () => getMyPartnerVerificationStatus(),
});

export const Route = createFileRoute("/_authenticated/partner/profile")({
  head: () => ({
    meta: [
      { title: "Partner profile — Shields" },
      {
        name: "description",
        content: "Edit the public profile customers see in the Shields directory.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PartnerProfilePage,
});

function PartnerProfilePage() {
  const {data:session}=useSuspenseQuery(sessionQuery),{data:services}=useSuspenseQuery(servicesQuery),{data:existing}=useSuspenseQuery(profileQuery),{data:verification}=useSuspenseQuery(verificationQuery);
  const [tab,setTab]=useState<"overview"|"services"|"gallery"|"reviews">("overview");
  const [editing,setEditing]=useState(false);
  const save=useServerFn(upsertMyPartnerProfile);
  const [tradingName,setTradingName]=useState(existing?.trading_name??""),[headline,setHeadline]=useState(existing?.headline??""),[intro,setIntro]=useState(existing?.intro??""),[serviceAreas,setServiceAreas]=useState(existing?.service_areas??""),[selected,setSelected]=useState<string[]>(existing?.services??[]),[published,setPublished]=useState(existing?.published??false),[saving,setSaving]=useState(false);
  const initials=(tradingName||"Shields Partner").split(/\s+/).slice(0,2).map(x=>x[0]).join("").toUpperCase();
  const email=(session as any)?.email??"";
  const phone=(existing as any)?.phone??(session as any)?.phone??"";
  async function submit(e:FormEvent){e.preventDefault();setSaving(true);const res=await save({data:{tradingName,headline:headline||null,intro:intro||null,services:selected,serviceAreas:serviceAreas||null,published}});setSaving(false);if(!res.ok){toast.error(res.error??"Could not save profile");return}toast.success("Business profile saved");setEditing(false)}
  return <div className="partner-profile-ref-page"><main className="partner-profile-ref-main">
    <header><h1>Business Profile</h1></header>
    <nav className="partner-profile-ref-tabs">{(["overview","services","gallery","reviews"] as const).map(t=><button key={t} className={tab===t?"active":""} onClick={()=>setTab(t)}>{(t[0]??"").toUpperCase()+t.slice(1)}</button>)}</nav>
    {tab==="overview"&&<div className="partner-profile-ref-content">
      <section className="partner-profile-ref-identity"><div className="partner-profile-ref-avatar">{initials}</div><span><strong>{tradingName||"Your business"}</strong>{verification.approved?<em><ShieldCheck/> Verified Partner</em>:<small><Link to="/partner/verification">Verification status: {String(verification.application?.status ?? "not submitted").replaceAll("_"," ")}</Link></small>}</span><button onClick={()=>setEditing(true)}>Edit</button></section>
      <section className="partner-profile-ref-card"><h2>About us</h2><p>{intro||headline||"Add a short introduction so customers know what your business does and what they can expect."}</p></section>
      <section className="partner-profile-ref-card"><h2>Contact details</h2>{email&&<p><Mail/>{email}</p>}{phone&&<p><Phone/>{phone}</p>}{!email&&!phone&&<small>Add your contact details to your account to show them here.</small>}</section>
      {editing&&<form onSubmit={submit} className="partner-profile-ref-editor"><h2>Edit profile</h2><Label>Trading name</Label><Input value={tradingName} onChange={e=>setTradingName(e.target.value)} required/><Label>Headline</Label><Input value={headline} onChange={e=>setHeadline(e.target.value)}/><Label>About us</Label><Textarea rows={4} value={intro} onChange={e=>setIntro(e.target.value)}/><Label>Areas covered</Label><Input value={serviceAreas} onChange={e=>setServiceAreas(e.target.value)}/>{verification.approved&&<label className="partner-profile-ref-publish"><span><strong>Show me in the customer directory</strong><small>{published?"Your profile is live and customers can find you.":"Your profile is hidden until you publish it."}</small></span><Switch checked={published} onCheckedChange={v=>setPublished(v===true)}/></label>}{!verification.approved&&<p className="text-sm text-muted-foreground">Your business profile can be published after Shields approves your Partner application. <Link to="/partner/verification" className="font-bold text-primary underline">View verification status</Link>.</p>}<div><button type="button" onClick={()=>setEditing(false)}>Cancel</button><Button disabled={saving}>{saving?"Saving…":"Save changes"}</Button></div></form>}
    </div>}
    {tab==="services"&&<section className="partner-profile-ref-card partner-profile-ref-services"><h2>Services</h2><p>Choose the services you genuinely provide. These also help Shields match relevant opportunities.</p>{services.map(s=><label key={s.id}><Checkbox checked={selected.includes(s.slug)} onCheckedChange={()=>setSelected(p=>p.includes(s.slug)?p.filter(x=>x!==s.slug):[...p,s.slug])}/><span><strong>{s.name}</strong><small>{s.description}</small></span></label>)}<Button onClick={(e:any)=>void submit(e)}>Save services</Button></section>}
    {tab==="gallery"&&<section className="partner-profile-ref-card"><h2>Completed work</h2><p>Your gallery is your social-style portfolio of finished jobs. Add genuine work photos customers can browse.</p><WorkPhotosSection/></section>}
    {tab==="reviews"&&<section className="partner-profile-ref-card partner-profile-ref-reviews"><Star/><h2>Customer reviews</h2><p>Verified Shields reviews from completed jobs will appear here. Reviews can't be created or edited by Partners.</p><Link to="/partner/jobs">View completed jobs <ChevronRight/></Link></section>}
  </main></div>
}
function WorkPhotosSection() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["my-work-photos"],
    queryFn: () => listMyWorkPhotos(),
  });
  const [busy, setBusy] = useState(false);

  async function handleFiles(fileList: FileList | null) {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;
    setBusy(true);
    try {
      for (const file of files) {
        const path = await uploadPhoto("work-photos", file);
        const result = await addWorkPhoto({ data: { imagePath: path } });
        if (!result.ok) {
          toast.error(result.error ?? "Could not add photo");
          break;
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["my-work-photos"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(photoId: string) {
    const result = await deleteWorkPhoto({ data: { photoId } });
    if (!result.ok) {
      toast.error(result.error ?? "Could not remove photo");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["my-work-photos"] });
  }

  const photos = data?.photos ?? [];
  const limit = data?.limit ?? 6;

  return (
    <section className="pt-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-bold">Work portfolio</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Show customers your finished jobs. Photos appear on your public profile.
          </p>
        </div>
        <span className="text-xs font-semibold text-muted-foreground">
          {photos.length} of {limit} photos
        </span>
      </div>
      {photos.length > 0 && (
        <ul className="mt-4 grid grid-cols-2 gap-5 sm:grid-cols-3">
          {photos.map((p) => (
            <li key={p.id} className="relative overflow-hidden rounded-lg border border-border">
              {p.url && (
                <img
                  src={p.url}
                  alt={p.caption ?? "Your work photo"}
                  className="aspect-square w-full object-cover"
                />
              )}
              <button
                type="button"
                onClick={() => handleDelete(p.id)}
                aria-label="Remove photo"
                className="absolute right-2 top-2 rounded-full bg-background/90 p-1.5 text-destructive shadow-sm"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4">
        <Label htmlFor="workPhotos" className="sr-only">
          Add work photos
        </Label>
        <Input
          id="workPhotos"
          type="file"
          accept="image/*"
          multiple
          disabled={busy || photos.length >= limit}
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {photos.length >= limit && (
          <p className="mt-2 text-xs text-muted-foreground">
            Portfolio full —{" "}
            <Link to="/plans" className="font-semibold text-primary underline">
              upgrade your plan
            </Link>{" "}
            for more space.
          </p>
        )}
      </div>
    </section>
  );
}
